/**
 * Kalendertage als ISO-Datum `YYYY-MM-DD` (ohne Uhrzeit, ohne Zeitzone). Jahre nur 1900–2999 – aus KaBa
 * übernommen: ältere Zeitpunkte liefert PostgreSQL in Europe/Berlin mit Sekunden-Versatz, die JavaScript nicht lesen
 * kann; beim Tippen entstehen sonst unsinnige Zwischenwerte.
 */
export const DAY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

export function isPlausibleDay(value: string): boolean {
  const m = DAY_PATTERN.exec(value);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  if (y < 1900 || y > 2999 || mo < 1 || mo > 12 || d < 1) return false;
  const days = new Date(Date.UTC(y, mo, 0)).getUTCDate();
  return d <= days;
}

/** Heute als Kalendertag in einer Zeitzone */
const formatters = new Map<string, Intl.DateTimeFormat>();
export function todayIn(timeZone: string, now = new Date()): string {
  let f = formatters.get(timeZone);
  if (!f) formatters.set(timeZone, (f = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' })));
  return f.format(now);
}

/** Kalendertag + n Tage (UTC-Rechnung, keine Sommerzeit-Fallen) */
export function addDays(day: string, n: number): string {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/** Kalendertag + n Monate; Monatsende wird gekappt (31.01. + 1 Monat = 28./29.02.) */
export function addMonths(day: string, n: number): string {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  const total = y * 12 + (m - 1) + n;
  const ty = Math.floor(total / 12);
  const tm = total - ty * 12;
  const last = new Date(Date.UTC(ty, tm + 1, 0)).getUTCDate();
  return `${String(ty).padStart(4, '0')}-${String(tm + 1).padStart(2, '0')}-${String(Math.min(d, last)).padStart(2, '0')}`;
}

/** Wiederkehrende Aufgaben (P19): Einheit, Abstand und Bezug (ab Termin oder ab dem Tag des Erledigens) */
export const REPEAT_UNITS = ['day', 'week', 'month', 'year'] as const;
export type RepeatUnit = (typeof REPEAT_UNITS)[number];
export const REPEAT_BASES = ['due', 'done'] as const;
export type RepeatBasis = (typeof REPEAT_BASES)[number];
export interface Repeat {
  unit: RepeatUnit;
  interval: number;
  basis: RepeatBasis;
}

/** Nächster Termin ab einem Tag; Werktags-Regeln gibt es bewusst nicht (klein anfangen) */
export function nextOccurrence(from: string, r: Repeat): string {
  switch (r.unit) {
    case 'day':
      return addDays(from, r.interval);
    case 'week':
      return addDays(from, 7 * r.interval);
    case 'month':
      return addMonths(from, r.interval);
    case 'year':
      return addMonths(from, 12 * r.interval);
  }
}

/** Wochentag 0 = Sonntag … 6 = Samstag */
export function weekday(day: string): number {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

/** Abstand in Tagen (b − a) */
export function daysBetween(a: string, b: string): number {
  const ms = (s: string) => {
    const [y, m, d] = s.split('-').map(Number) as [number, number, number];
    return Date.UTC(y, m - 1, d);
  };
  return Math.round((ms(b) - ms(a)) / 86_400_000);
}

/** Schnellwahl für Termine (Regel R4: Termin direkt setzen, ohne Kalenderwerkzeug) */
export function quickDays(today: string) {
  const wd = weekday(today);
  return {
    today,
    tomorrow: addDays(today, 1),
    // „Nächste Woche“ = kommender Montag (heute Montag → in sieben Tagen)
    nextMonday: addDays(today, ((8 - wd) % 7) || 7),
    inOneWeek: addDays(today, 7),
  };
}

export type DueState = 'overdue' | 'today' | 'soon' | 'later';

/** Einordnung eines Termins für die Anzeige; „bald“ = innerhalb der nächsten 6 Tage */
export function dueState(due: string, today: string): DueState {
  const diff = daysBetween(today, due);
  if (diff < 0) return 'overdue';
  if (diff === 0) return 'today';
  if (diff <= 6) return 'soon';
  return 'later';
}

/**
 * Vorlagen G05: Werktage (Montag–Freitag) addieren – ohne Feiertagskalender, so auch benannt („Werktage ohne
 * Feiertage“). n < 0 zählt rückwärts; der Starttag selbst zählt nicht.
 */
export function addWorkdays(day: string, n: number, isHoliday?: (day: string) => boolean): string {
  let d = day;
  let left = Math.abs(n);
  const step = n < 0 ? -1 : 1;
  while (left > 0) {
    d = addDays(d, step);
    const wd = new Date(`${d}T12:00:00Z`).getUTCDay();
    // ADR 0102: Feiertage der Arbeitsgruppe zählen wie Wochenenden
    if (wd !== 0 && wd !== 6 && !isHoliday?.(d)) left--;
  }
  return d;
}

/** Termin aus einem Stichtag: Kalendertage oder Werktage Abstand (mit Feiertagskalender der Arbeitsgruppe) */
export function fromAnchor(anchor: string, offset: number, unit: 'days' | 'workdays', isHoliday?: (day: string) => boolean): string {
  return unit === 'workdays' ? addWorkdays(anchor, offset, isHoliday) : addDays(anchor, offset);
}

/** Kalendertag „JJJJ-MM-TT“ als „TT.MM.JJJJ“ (Server-Texte, z. B. Regel-Protokolle) */
export function formatDayDe(day: string): string {
  const [y, m, d] = day.split('-');
  return `${d}.${m}.${y}`;
}

/* ---------- Termine mit Uhrzeit (W3, ADR 0072) ---------- */

const zoneFmt = new Map<string, Intl.DateTimeFormat>();
function partsIn(ms: number, timeZone: string) {
  let f = zoneFmt.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', { timeZone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    zoneFmt.set(timeZone, f);
  }
  const p = Object.fromEntries(f.formatToParts(new Date(ms)).map((x) => [x.type, x.value]));
  return { y: +p.year!, mo: +p.month!, d: +p.day!, h: +p.hour! % 24, mi: +p.minute!, s: +p.second! };
}
/** Abstand der Zone zu UTC zum Zeitpunkt `ms` (in ms, positiv östlich von UTC) */
function offsetAt(ms: number, timeZone: string): number {
  const p = partsIn(ms, timeZone);
  return Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi, p.s) - Math.floor(ms / 1000) * 1000;
}
export function isTimeZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return true;
  } catch {
    return false;
  }
}
/** „HH:MM“ */
export const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

/**
 * Ortszeit (Kalendertag + Uhrzeit in einer IANA-Zone) → Zeitpunkt. Zur Zeitumstellung gibt es Uhrzeiten nicht
 * (`gap`, Frühjahr) oder doppelt (`ambiguous`, Herbst) – dann entscheidet die Person, nie still der Server.
 */
export function zonedToUtc(day: string, time: string, timeZone: string): { kind: 'ok'; at: Date } | { kind: 'gap' } | { kind: 'ambiguous'; earlier: Date; later: Date } {
  const [y, mo, d] = day.split('-').map(Number) as [number, number, number];
  const [h, mi] = time.split(':').map(Number) as [number, number];
  const guess = Date.UTC(y, mo - 1, d, h, mi);
  const found = new Set<number>();
  for (const probe of [guess - 36 * 3_600_000, guess, guess + 36 * 3_600_000]) {
    const t = guess - offsetAt(probe, timeZone);
    const p = partsIn(t, timeZone);
    if (p.y === y && p.mo === mo && p.d === d && p.h === h && p.mi === mi) found.add(t);
  }
  const all = [...found].sort((a, b) => a - b);
  if (!all.length) return { kind: 'gap' };
  if (all.length === 1) return { kind: 'ok', at: new Date(all[0]!) };
  return { kind: 'ambiguous', earlier: new Date(all[0]!), later: new Date(all.at(-1)!) };
}

/** Zeitpunkt → Kalendertag und Uhrzeit in einer Zone */
export function utcToZoned(at: Date | string, timeZone: string): { day: string; time: string } {
  const p = partsIn(new Date(at).getTime(), timeZone);
  const pad = (n: number) => String(n).padStart(2, '0');
  return { day: `${p.y}-${pad(p.mo)}-${pad(p.d)}`, time: `${pad(p.h)}:${pad(p.mi)}` };
}

/* ---------- Terminserien (ADR 0079) ---------- */

export const SERIES_UNITS = ['day', 'week', 'month'] as const;
export type SeriesUnit = (typeof SERIES_UNITS)[number];
export interface SeriesRule {
  unit: SeriesUnit;
  interval: number;
  /** letzter möglicher Tag (einschließlich) */
  until?: string | null;
  /** Anzahl der Termine (einschließlich des ersten) */
  count?: number | null;
}
export const SERIES_MAX = 60;

/**
 * Tage einer Serie ab `start`: immer vom ersten Tag aus gerechnet (Monatsende gekappt, kein Abdriften), höchstens
 * `SERIES_MAX` Termine und höchstens zwei Jahre.
 */
export function seriesDays(start: string, r: SeriesRule): string[] {
  const limit = addMonths(start, 24);
  const end = r.until && r.until < limit ? r.until : limit;
  const max = Math.min(r.count ?? SERIES_MAX, SERIES_MAX);
  const out: string[] = [];
  for (let k = 0; out.length < max; k++) {
    const d = r.unit === 'day' ? addDays(start, k * r.interval) : r.unit === 'week' ? addDays(start, 7 * k * r.interval) : addMonths(start, k * r.interval);
    if (d > end) break;
    out.push(d);
  }
  return out;
}

